/**
 * usePublish Hook
 *
 * Manages publish and rollback operations for the Home Layout Manager.
 * Handles publishing draft to live, viewing history, and reverting to previous states.
 *
 * @module HomeLayoutManager/hooks/usePublish
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useNotify } from 'react-admin';
import { httpClient } from '../../../components/fetchApiData';
import config from '../../../config';
import Logger from '../../../utils/Logger';
import type {
  PublishResponse,
  PublishHistoryItem,
  PublishHistoryResponse,
  DragOperation,
} from '../types';

const API_BASE = `${config.API_URL}/admin/home-layout`;

/**
 * Options for the usePublish hook
 */
interface UsePublishOptions {
  /** Callback when publish succeeds */
  onPublishSuccess?: (response: PublishResponse) => void;
  /** Callback when rollback succeeds */
  onRollbackSuccess?: () => void;
  /** Callback when an error occurs */
  onError?: (error: Error) => void;
}

/**
 * Return type for the usePublish hook
 */
interface UsePublishReturn {
  /** Whether a publish/rollback operation is in progress */
  isPublishing: boolean;
  /** Whether publish history is being loaded */
  isLoadingHistory: boolean;
  /** List of publish history items */
  publishHistory: PublishHistoryItem[];
  /** Total number of history items */
  historyTotal: number;
  /** Whether more history items are available */
  historyHasMore: boolean;
  /** Publish draft changes to live database */
  publish: (notes?: string, operations?: DragOperation[]) => Promise<PublishResponse | null>;
  /** Fetch publish history for rollback capability */
  fetchPublishHistory: (page?: number) => Promise<void>;
  /** Rollback draft to a previous published state */
  rollback: (publishId: string) => Promise<void>;
}

/**
 * Hook for managing publish and rollback operations
 *
 * @param options - Configuration options for the hook
 * @returns Object containing publish state and actions
 *
 * @example
 * ```tsx
 * const { publish, rollback, isPublishing } = usePublish({
 *   onPublishSuccess: (result) => console.log('Published!', result),
 * });
 *
 * // Publish with notes
 * await publish('Updated homepage banners');
 *
 * // Rollback to previous state
 * await rollback('publish_123');
 * ```
 */
export function usePublish(options: UsePublishOptions = {}): UsePublishReturn {
  const { onPublishSuccess, onRollbackSuccess, onError } = options;
  const notify = useNotify();

  // Use refs for callbacks to avoid useCallback dependency issues
  const onPublishSuccessRef = useRef(onPublishSuccess);
  const onRollbackSuccessRef = useRef(onRollbackSuccess);
  const onErrorRef = useRef(onError);

  // Keep refs up to date
  useEffect(() => {
    onPublishSuccessRef.current = onPublishSuccess;
    onRollbackSuccessRef.current = onRollbackSuccess;
    onErrorRef.current = onError;
  }, [onPublishSuccess, onRollbackSuccess, onError]);

  const [isPublishing, setIsPublishing] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [publishHistory, setPublishHistory] = useState<PublishHistoryItem[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyHasMore, setHistoryHasMore] = useState(false);

  /**
   * Publish draft changes to live database
   *
   * @param notes - Optional notes describing the changes being published
   * @returns The publish response or null if failed
   */
  const publish = useCallback(async (notes?: string, operations?: DragOperation[]): Promise<PublishResponse | null> => {
    setIsPublishing(true);

    try {
      const { json } = await httpClient(`${API_BASE}/publish`, {
        method: 'POST',
        body: JSON.stringify({ notes, operations: operations || [] }),
      });
      const publishResult = json as PublishResponse;

      notify(
        `Published successfully! ${publishResult.changesSummary.bannersModified} banner(s) and ${publishResult.changesSummary.categoriesModified} category(ies) updated.`,
        { type: 'success' }
      );

      onPublishSuccessRef.current?.(publishResult);
      return publishResult;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to publish changes';
      notify(errorMessage, { type: 'error' });
      onErrorRef.current?.(err instanceof Error ? err : new Error(errorMessage));
      return null;
    } finally {
      setIsPublishing(false);
    }
  }, [notify]); // Stable dependencies - no callbacks

  /**
   * Fetch publish history for rollback capability
   *
   * @param page - Page number to fetch (1-indexed, defaults to 1)
   */
  const fetchPublishHistory = useCallback(async (page = 1) => {
    setIsLoadingHistory(true);

    try {
      const queryParams = new URLSearchParams({
        page: String(page),
        limit: '10',
      });
      const { json } = await httpClient(`${API_BASE}/publish-history?${queryParams}`, {
        method: 'GET',
      });
      const response = json as PublishHistoryResponse;

      if (page === 1) {
        setPublishHistory(response.data);
      } else {
        setPublishHistory(prev => [...prev, ...response.data]);
      }
      setHistoryTotal(response.total);
      setHistoryHasMore(response.hasMore);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load publish history';
      notify(errorMessage, { type: 'error' });
      onErrorRef.current?.(err instanceof Error ? err : new Error(errorMessage));
    } finally {
      setIsLoadingHistory(false);
    }
  }, [notify]); // Stable dependencies - no callbacks

  /**
   * Rollback draft to a previous published state
   *
   * @param publishId - ID of the publish history entry to rollback to
   */
  const rollback = useCallback(async (publishId: string) => {
    setIsPublishing(true);

    try {
      await httpClient(`${API_BASE}/rollback/${publishId}`, {
        method: 'POST',
      });

      notify('Rolled back to previous state. Review and publish when ready.', { type: 'success' });
      onRollbackSuccessRef.current?.();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to rollback';
      notify(errorMessage, { type: 'error' });
      onErrorRef.current?.(err instanceof Error ? err : new Error(errorMessage));
    } finally {
      setIsPublishing(false);
    }
  }, [notify]); // Stable dependencies - no callbacks

  return {
    isPublishing,
    isLoadingHistory,
    publishHistory,
    historyTotal,
    historyHasMore,
    publish,
    fetchPublishHistory,
    rollback,
  };
}

export default usePublish;
